'use client';

/**
 * PageSkeleton Component
 *
 * A pre-built page-level skeleton matching the PageTemplate layout used in portals.
 * Provides configurable header, breadcrumbs, content sections, and sidebar placeholders.
 */

import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import type React from 'react';
import ShimmerLoader from '../ShimmerLoader';

// ============================================================================
// Types
// ============================================================================

export interface PageSkeletonProps {
  /** Show header skeleton (title + action buttons) */
  header?: boolean;

  /** Show breadcrumb skeleton */
  breadcrumbs?: boolean;

  /** Number of content section skeletons (default 0) */
  sections?: number;

  /** Show sidebar skeleton */
  sidebar?: boolean;

  /** Additional styles */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Sub-components
// ============================================================================

const BreadcrumbsSkeleton: React.FC = () => (
  <Box
    data-testid="page-skeleton-breadcrumbs"
    sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, mb: 2 }}
  >
    <ShimmerLoader variant="text" width={60} height={16} />
    <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.disabled' }} />
    <ShimmerLoader variant="text" width={80} height={16} />
    <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.disabled' }} />
    <ShimmerLoader variant="text" width={100} height={16} />
  </Box>
);

const HeaderSkeleton: React.FC = () => (
  <Box
    data-testid="page-skeleton-header"
    sx={{
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      mb: 3,
    }}
  >
    {/* Title + subtitle */}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <ShimmerLoader variant="text" width={240} height={32} />
      <ShimmerLoader variant="text" width={160} height={18} />
    </Box>
    {/* Action buttons */}
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
      <ShimmerLoader variant="button" width={100} height={36} />
      <ShimmerLoader variant="button" width={100} height={36} />
    </Box>
  </Box>
);

const SectionSkeleton: React.FC<{ index: number }> = ({ index }) => (
  <Box
    data-testid={`page-skeleton-section-${index}`}
    sx={{
      borderRadius: 2,
      p: 3,
      bgcolor: 'background.paper',
      mb: 2,
    }}
  >
    <ShimmerLoader variant="text" width={180} height={24} />
    <ShimmerLoader variant="rectangular" width="100%" height={120} sx={{ mt: 2, borderRadius: 1 }} />
    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <ShimmerLoader variant="text" width="100%" height={14} />
      <ShimmerLoader variant="text" width="85%" height={14} />
      <ShimmerLoader variant="text" width="60%" height={14} />
    </Box>
  </Box>
);

const SidebarSkeleton: React.FC = () => (
  <Box
    data-testid="page-skeleton-sidebar"
    sx={{
      width: 280,
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    }}
  >
    <ShimmerLoader variant="rounded" width="100%" height={140} />
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 1 }}>
      <ShimmerLoader variant="text" width="80%" height={16} />
      <ShimmerLoader variant="text" width="60%" height={14} />
      <ShimmerLoader variant="text" width="70%" height={14} />
      <ShimmerLoader variant="text" width="50%" height={14} />
    </Box>
  </Box>
);

// ============================================================================
// Component
// ============================================================================

const PageSkeleton: React.FC<PageSkeletonProps> = ({
  header = false,
  breadcrumbs = false,
  sections = 0,
  sidebar = false,
  sx,
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, ...sx }}>
      {/* Main content area */}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        {breadcrumbs && <BreadcrumbsSkeleton />}
        {header && <HeaderSkeleton />}
        {Array.from({ length: sections }).map((_, index) => (
          <SectionSkeleton key={index} index={index} />
        ))}
      </Box>

      {/* Sidebar */}
      {sidebar && <SidebarSkeleton />}
    </Box>
  );
};

PageSkeleton.displayName = 'PageSkeleton';

export { PageSkeleton };
export default PageSkeleton;
